import {
  ButtonDS,
  DialogModalDS,
  InputDS,
  Spacer,
} from "@qivia/ui";
import { Folder } from "@qivia/ui/src/assets/assets";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { selectDepartments } from "../departmentsSlice";
import { useAppSelector } from "../../../../redux/hooks";
import { DepartmentType } from "../departmentsApi";

export const AddDepartmentModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const [department, setDepartment] = useState<string>("");
  const departments = useAppSelector(selectDepartments);
  
  const isDepartmentNameDuplicate: boolean = useMemo(() => {
    if (department === "") return false;

    return departments.filter(
      (departmentItem: DepartmentType) =>
        departmentItem.name.toLocaleUpperCase().trim() === department.trim(),
    ).length > 0;
  }, [departments, department]);
  
  const isValidForm: boolean = useMemo(
    () => {
      return (
        department.length <= 28 &&
        department !== "" &&
        !isDepartmentNameDuplicate
      );
    },
    [isDepartmentNameDuplicate, department],
  );

  const DepartmentError: string | undefined = useMemo(() => {
    if (isDepartmentNameDuplicate) {
      return t("departments.modal.error.nameAlreadyExists");
    }

    return department.length > 28
      ? t("departments.modal.error.length")
      : undefined;
  }, [isDepartmentNameDuplicate, department]);

  const onSubmit = useCallback(() => {
    console.log("department added : ", department);
    props.onCloseModal();
  }, [props, department]);
  
  const clickToSubmit = useCallback(() => {
    isValidForm && onSubmit();
  }, [isValidForm, onSubmit]);

  const resetValue = useCallback(() => {
    setDepartment("");
  }, []);

  const handleCloseModal = () => {
    resetValue();
    props.onCloseModal();
  }

  useEffect(() => {
    if (props.visible === false) {
      resetValue();
    }
  }, [props.visible, resetValue]);

  return (
    <DialogModalDS
      visible={props.visible}
      title={t("departments.modal.title") || ""}
      subTitle={t("departments.modal.subTitle") || ""}
      iconTitle={<Folder />}
      onClose={handleCloseModal}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("departments.modal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={handleCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("departments.modal.add")}
            format={"fill"}
            buttonType={"primary"}
            onClick={() => {
                clickToSubmit();
            }}
            disabled={!isValidForm}
          />
        ),
      }}
    >
      <StyledModalContent>
        <InputDS
          label={t("departments.modal.department") || ""}
          placeholder={t("departments.modal.department.placeholder") || ""}
          error={DepartmentError}
          value={department}
          onChange={(e) => setDepartment(e.target.value.toLocaleUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && clickToSubmit()}
          withoutSpacerBottom
        />
        <Spacer y={0.625} />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
